import {Box} from '@dropbox/dig-foundations';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {growthbookCacheAtom} from 'atoms/growthbook';
import {queryFromParam} from 'components/search/box/GlobalSearchBox';
import {SearchBox} from 'components/search/box/SearchBox';
import {ROUTE_PATHS} from 'constant';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {CSSProperties, useEffect, useRef, useState} from 'react';
import {Navigate, useSearchParams} from 'react-router';

import {useOpenOutsideInNewWindow} from './useOpenOutsideInNewWindow';
import {useSearchAppManifest} from './useSearchAppManifest';
import {useTitleBarColor} from './useTitleBarColor';

/** The box's header (16 + 28 + 16, plus its hairline). */
const HEADER_HEIGHT = 61;
/**
 * The installed window's size the first time it opens: room for the list and
 * the profile pane beside it, a good deal smaller than the browser's default.
 */
const FIRST_WINDOW = {width: 960, height: 680};
/** Bump the version to resize windows already installed at an old size. */
const SIZED_KEY = 'search-app:sized:2';
/**
 * With the title bar hidden (window controls overlay), the page runs to the
 * top of the window, so this strip leaves the traffic lights their room and
 * drags the window like the bar did. Without it, the strip is zero tall.
 */
const TITLE_BAR = 'env(titlebar-area-height, 0px)';
const titleBarStyle = {
  height: TITLE_BAR,
  appRegion: 'drag',
  WebkitAppRegion: 'drag',
} as CSSProperties;

const useWindowWidth = () => {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
};

/**
 * The first time the installed app opens, size its window to the box. After
 * that the browser remembers whatever size it's left at.
 */
const useFirstWindowSize = () => {
  useEffect(() => {
    if (!window.matchMedia('(display-mode: standalone)').matches) return;
    if (localStorage.getItem(SIZED_KEY)) return;
    localStorage.setItem(SIZED_KEY, '1');
    window.resizeTo(FIRST_WINDOW.width, FIRST_WINDOW.height);
  }, []);
};

/**
 * The search box filling the window, flat: the window is already the app, so
 * no box floats inside it.
 */
const SearchAppPage = () => {
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(() => queryFromParam(searchParams.get('s')));
  const width = useWindowWidth();
  const pageRef = useRef<HTMLDivElement>(null);
  useSearchAppManifest();
  useTitleBarColor(pageRef);
  useOpenOutsideInNewWindow(ROUTE_PATHS.SEARCH_APP);
  useFirstWindowSize();
  useDocumentTitle(t('searching') + (query.length > 0 ? ` "${query}"` : '...'));

  useEffect(() => {
    analyticsLogger().logEvent('SEARCH_SHOWN', {
      source: window.matchMedia('(display-mode: standalone)').matches ? 'app' : 'app_browser',
      search_box: 'relaunch',
    });
  }, []);

  // There's nothing to close here, so Escape clears the field instead (the
  // box's own Escape, closing the pane, comes first and stops there).
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) setQuery('');
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <Box ref={pageRef} backgroundColor="Background Base" style={{minHeight: '100vh'}}>
      <Box style={titleBarStyle} />
      <SearchBox
        query={query}
        setQuery={setQuery}
        logSelections
        entryPoint="app"
        flat
        width={width}
        bodyHeight={`calc(100vh - ${TITLE_BAR} - ${HEADER_HEIGHT}px)`}
      />
    </Box>
  );
};

/**
 * The search box on its own page, installable as its own small app (a
 * window with just search and Ask). Behind `ask_relaunch`; without it, home.
 */
export const SearchApp = () => {
  const {isAskRelaunchEnabled} = useAtomValue(growthbookCacheAtom);
  return isAskRelaunchEnabled ? <SearchAppPage /> : <Navigate to={ROUTE_PATHS.HOME} replace />;
};
